import { forwardRef, useMemo } from 'react';

import { useTranslateHeaders } from '@/ui/table/helpers/use-translate-headers';
import type { Row } from '@/ui/table/table.types';

import { styled } from 'goober';
import { useTranslation } from 'react-i18next';
import { contentItemsTableHeaders } from './content-item-table-configuration';
import { UserName } from '@/ui/typography/across-platform';
import { TagsPreview } from '@/features/tags/tags-preview';
import { ContentItemVisibilitySwitch } from './content-item-visibility-switch';
import { formatDate } from '@/helpers/format-date';
import { GENERAL_DATE_FORMAT } from '@/constants';
import type { ContentItemSortKey } from './feed.state';
import { ContentItemActionsMenu } from './content-item-actions-menu';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { useCurrentAccountData } from '@/hooks/use-current-account-data';
import { useContentItems } from './use-content-items.query';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';

export const useContentItemTableConfiguration = () => {
  const { t } = useTranslation('settings', {
    keyPrefix: 'feedTab',
  });

  const { data = [] } = useContentItems();
  const { data: accountData } = useCurrentAccountData();

  const header = useTranslateHeaders<ContentItemSortKey>(
    contentItemsTableHeaders,
    'feed',
  );

  const rows: Row[] = useMemo(() => {
    return data.map(contentItem => {
      return {
        id: contentItem.id,
        cells: [
          {
            value: (
              <Flexbox gap="16px" alignItems="center">
                {contentItem.image?.url ? (
                  <PreviewImage
                    alt={contentItem.title}
                    src={contentItem.image.url}
                  />
                ) : accountData?.logo.url ? (
                  <PreviewImage
                    alt={contentItem.title}
                    src={accountData.logo.url}
                  />
                ) : (
                  <ImagePlaceholder alt={contentItem.title} />
                )}
                <EllipsisTextTooltip
                  text={contentItem.title}
                  Component={<Title data-testid="content-item-name" />}
                ></EllipsisTextTooltip>
              </Flexbox>
            ),
          },
          {
            value: (
              <UserName>
                {formatDate(contentItem.uploadedDate, {
                  format: GENERAL_DATE_FORMAT,
                })}
              </UserName>
            ),
          },
          {
            value: <TagsPreview tags={contentItem.tags} maxVisibleTags={3} />,
          },
          {
            value: (
              <UserName data-testid="content-item-category">
                {t([`categories.${contentItem.category}`])}
              </UserName>
            ),
          },
          {
            value: (
              <ContentItemVisibilitySwitch
                contentItemId={contentItem.id}
                isVisible={contentItem.isVisible}
              />
            ),
          },
          {
            value: <ContentItemActionsMenu contentItem={contentItem} />,
          },
        ],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    rows,
    header,
  };
};

const PreviewImage = styled('img')`
  width: 46px;
  height: 46px;
  object-fit: cover;
`;

const ImagePlaceholder = styled('img')`
  width: 46px;
  height: 46px;
`;

const Title = styled(UserName, forwardRef)`
  ${({ theme }) => theme.mixins.ellipsis}
  max-width: 180px;
`;
